import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HeatmapPreviewMap from '../Heatmap/HeatmapPreviewMap';

const BusinessProfilePopup = ({ 
  isOpen, 
  onClose, 
  selectedBusiness, 
  formData 
}) => {
  if (!isOpen || !selectedBusiness) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 2000, // Higher than any modal
        backdropFilter: 'blur(2px)'
      }}
      onClick={onClose}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          width: '90vw',
          maxWidth: '900px',
          maxHeight: '85vh',
          overflow: 'auto',
          outline: 'none',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          transform: 'scale(1)',
          transition: 'all 0.2s ease-in-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ color: '#0B91D6', fontWeight: 700 }}>
            🗺️ Grid Performance Analysis
          </Typography>
          <IconButton 
            onClick={onClose}
            sx={{ 
              backgroundColor: '#f3f4f6',
              '&:hover': { backgroundColor: '#e5e7eb' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Business Info */}
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
            {selectedBusiness.isMyBusiness ? `${selectedBusiness.name} (YOUR BUSINESS)` : selectedBusiness.name}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0B91D6' }}>
                  #{selectedBusiness.rank || 'N/A'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Overall Rank
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                  {selectedBusiness.gridAppearances || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Grid Appearances
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                  #{selectedBusiness.averageRank?.toFixed(1) || 'N/A'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Average Rank
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#8b5cf6' }}>
                  {Math.round(selectedBusiness.gmbScore || 0)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  GMB Score
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Grid Map */}
        <Paper sx={{ p: 2, mb: 3, border: '1px solid #e2e8f0' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
            📍 Geographic Performance Map
          </Typography>
          <Box sx={{ height: '500px', width: '100%', borderRadius: 2, overflow: 'hidden' }}>
            <HeatmapPreviewMap 
              center={formData?.center}
              gridSize={selectedBusiness.gridData?.gridSize || 7}
              radiusKm={selectedBusiness.gridData?.searchRadius || 2}
              height={500}
              gridData={selectedBusiness.gridData}
              showRankings={true}
            />
          </Box>
        </Paper>

        {/* Legend */}
        <Paper sx={{ p: 3, backgroundColor: '#fafafa', border: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
            🎨 Performance Legend
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                backgroundColor: '#10b981', 
                borderRadius: '50%',
                border: '2px solid #059669'
              }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Rank 1-3 (Excellent)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                backgroundColor: '#f59e0b', 
                borderRadius: '50%',
                border: '2px solid #d97706'
              }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Rank 4-10 (Good)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                backgroundColor: '#ef4444', 
                borderRadius: '50%',
                border: '2px solid #dc2626'
              }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Rank 11+ (Needs Improvement)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                backgroundColor: '#9ca3af', 
                borderRadius: '50%',
                border: '2px solid #6b7280'
              }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Not Ranked
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body2" color="textSecondary" sx={{ mt: 3, textAlign: 'center', fontStyle: 'italic' }}>
            This map shows where "{selectedBusiness.name}" appears in search results across your analysis grid.
            Click any pin to see detailed coordinates and ranking information.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default BusinessProfilePopup;
